import { MdSearch } from "react-icons/md";
import { useEffect, useMemo, useState } from "react";
import ParavetModalComponent from "../ParavetModalComponent";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { getParavetClinicColumns } from "@/app/hooks/custom_hooks";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { IParavetClinicListItem } from "@/api_framework/api_modals/Configuration";
import { useParavetClinicListAPI } from "@/app/hooks/api_hooks/configuration/useParavetClinicListAPI";

const ClinicParavetMappingList = () => {
  const { NO_APPOINTMENTS } = useImageConstants();
  const { execute: fetchClinics, clinicList } = useParavetClinicListAPI();
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClinic, setSelectedClinic] = useState<IParavetClinicListItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = (clinic: IParavetClinicListItem) => {
    setSelectedClinic(clinic);
    setModalOpen(true);
  };
  const columns = getParavetClinicColumns(openModal);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    fetchClinics().then(() => {
      if (!active) return;
      setIsLoading(false);
    });
    return () => {
      active = false;
    };
  }, [fetchClinics]);

  const filteredClinics = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return clinicList;
    return clinicList.filter(
      (clinic: IParavetClinicListItem) =>
        clinic.clinic_name?.toLowerCase().includes(query) || clinic.id?.toLowerCase().includes(query),
    );
  }, [clinicList, searchQuery]);

  return (
    <div className="px-5 py-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Clinic List</h2>
          <p className="mt-1 text-sm text-slate-500">Map paravets to clinics</p>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full flex-col gap-2 sm:max-w-xl">
          <div className="flex items-center gap-2">
            <div className="relative w-full">
              <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search clinic name or ID"
                className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-10 text-sm text-slate-900 shadow-sm focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setSearchQuery("")}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <>
            <SkeletonType1 />
            <SkeletonType1 className="mt-4" />
          </>
        ) : filteredClinics.length === 0 ? (
          <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Clinic Data" description="No clinics found." />
        ) : (
          <div>
            <DataTable data={filteredClinics || []} columns={columns} maxHeight="600px" />
          </div>
        )}
      </div>

      {modalOpen && (
        <ParavetModalComponent
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          clinic={selectedClinic}
          onSubmitSuccess={() => fetchClinics()}
        />
      )}
    </div>
  );
};

export default ClinicParavetMappingList;
